import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, ArrowRight } from 'lucide-react';
import Testimonials from '../Testimonials';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

// Animation variants
const heroVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

// Sleep stories
const stories = [
  {
    title: "Biber auf Traumreise",
    image: "https://i.imgur.com/Nhw84Vg.png",
    audioUrl: '/biber-auf-traumreise.m4a'
  },
  {
    title: "Eli und die Mondwüste",
    image: "https://i.imgur.com/yEfe1fj.png",
    audioUrl: '/eli-und-die-mondwüste.m4a'
  }
];

const SleepLanding = () => {
  const [activeAudioIndex, setActiveAudioIndex] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef(null);
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const heroRef = useRef(null);

  const handleAudioPlay = (index) => {
    if (!audioRef.current) return;

    if (activeAudioIndex === index && isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      if (activeAudioIndex !== index) {
        audioRef.current.src = stories[index].audioUrl;
        audioRef.current.currentTime = 0;
        setAudioProgress(0);
      }
      audioRef.current.play();
      setIsAudioPlaying(true);
      setActiveAudioIndex(index);
    }
  };

  useEffect(() => {
    const audioElement = audioRef.current;
    
    let progressInterval;
    if (audioElement && isAudioPlaying) {
      progressInterval = setInterval(() => {
        const currentProgress = (audioElement.currentTime / audioElement.duration) * 100;
        setAudioProgress(currentProgress);
      }, 50);
    }

    const handleAudioEnd = () => {
      setIsAudioPlaying(false);
      setAudioProgress(0);
      setActiveAudioIndex(null);
    };

    if (audioElement) {
      audioElement.addEventListener('ended', handleAudioEnd);
    }

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (audioElement) {
        audioElement.removeEventListener('ended', handleAudioEnd);
      }
    };
  }, [isAudioPlaying]);

  const trackEvent = (category, label, value) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', label, {
        event_category: category,
        event_label: label,
        value: value
      });
    }
  };

  // Add scroll visibility logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeroVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '-80px',
        threshold: 0
      }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <audio ref={audioRef} className="hidden">
        <source src="" type="audio/mp4" />
      </audio>

      {/* Navigation Bar */}
      <nav className="fixed top-0 z-50 w-full">
        <div 
          className="w-full"
          style={{
            backgroundImage: 'url(/nav-bg.svg)',
            backgroundSize: 'auto 120%',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat',
          }}
        >
          <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 xl:px-6 relative">
            <div className="flex items-center justify-between h-16 lg:h-24">
              {/* Logo - always on the left for desktop */}
              <div className="hidden lg:block">
                <Link to="/" className="flex items-center">
                  <img 
                    src="/Logo.svg" 
                    alt="Plottie Logo" 
                    className="h-12 w-auto"
                  />
                </Link>
              </div>

              {/* Mobile logo with fade and slide animation */}
              <div className="lg:hidden">
                {/* Centered mobile logo */}
                <div 
                  className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 ${
                    isHeroVisible 
                      ? 'opacity-100 -translate-y-1/2' 
                      : 'opacity-0 -translate-y-full'
                  }`}
                  style={{
                    transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Link to="/" className="flex items-center">
                    <img 
                      src="/Logo.svg" 
                      alt="Plottie Logo" 
                      className="h-8 w-auto"
                    />
                  </Link>
                </div>

                {/* Left-aligned mobile logo */}
                <div 
                  className={`absolute left-4 sm:left-6 top-1/2 transform ${
                    isHeroVisible 
                      ? 'opacity-0 -translate-y-full' 
                      : 'opacity-100 -translate-y-1/2'
                  }`}
                  style={{
                    transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Link to="/" className="flex items-center">
                    <img 
                      src="/Logo.svg" 
                      alt="Plottie Logo" 
                      className="h-8 w-auto"
                    />
                  </Link>
                </div>
              </div>
              
              {/* Button - always visible on desktop */}
              <div className="hidden lg:block">
                <a 
                  href="/erstellen"
                  onClick={() => {
                    trackEvent('engagement', 'create_story_button_sleep_nav_desktop', 1);
                  }}
                  className="px-5 py-3 bg-[#FFA23E] text-[#343434] rounded-full font-freeman text-lg md:tracking-[-0.04rem] hover:bg-[#ff9625] transition-all duration-300"
                >
                  Geschichte erstellen
                </a>
              </div>

              {/* Mobile button with fade animation */}
              <div 
                className={`lg:hidden ${
                  isHeroVisible ? 'opacity-0' : 'opacity-100'
                }`}
                style={{
                  transition: 'all 600ms ease-out'
                }}
              >
                <a 
                  href="/erstellen"
                  onClick={() => {
                    trackEvent('engagement', 'create_story_button_sleep_nav_mobile', 1);
                  }}
                  className="px-3 py-2 bg-[#FFA23E] text-[#343434] rounded-full font-freeman text-base hover:bg-[#ff9625] transition-all duration-300 tracking-[-0.03rem]"
                >
                  Geschichte erstellen
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <motion.div 
        ref={heroRef}
        className="sm:mx-0 pt-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 py-16 md:pt-56 md:pb-32 flex flex-col items-center md:h-auto md:justify-start">
          <div className="w-full text-center">
            <div className="max-w-[90%] md:max-w-[80%] mx-auto">
              <motion.h1 
                variants={heroVariants}
                className="text-[#343434] text-[2.8rem] sm:text-[3.25rem] md:text-[4.5rem] leading-[110%] tracking-[-0.105rem] md:tracking-[-0.135rem] font-freeman mb-3 md:mb-6"
              >
                Sanft in den Schlaf - mit personalisierten Einschlafgeschichten für dein Kind.
              </motion.h1>
              <motion.p 
                variants={heroVariants}
                className="text-[#343434] text-base md:text-[1.25rem] font-normal leading-[150%] font-aleo"
              >
                Mit personalisierten Einschlafgeschichten, die speziell auf die Bedürfnisse deines Kindes zugeschnitten sind, schaffst du entspannte Abendrituale und eine beruhigende Einschlafzeit.
              </motion.p>
              
              <motion.div 
                variants={heroVariants}
                className="relative z-10 mt-8 md:mt-12"
              >
                <div className="max-w-2xl mx-auto text-center flex flex-col items-center">
                  <a 
                    href="/erstellen"
                    className="h-14 bg-[#FFA23E] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300"
                  >
                    Jetzt Geschichte erstellen
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Why Sleep Matters Section */}
      <motion.section
        className="w-full bg-[#FFF3DD] py-40 md:py-[24rem] relative"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Add decorative image */}
        <div className="w-full absolute -top-[1px] select-none pointer-events-none" aria-hidden="true">
          <img
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/footer-deco-qeD3pts3cCvDUvK9Y5X2MMgg2w172y.png"
            alt=""
            className="w-full"
          />
        </div>

        <div className="max-w-full sm:max-w-[95%] xl:max-w-[80%] 4xl:max-w-[90rem] mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:items-center md:gap-16 lg:gap-24 py-0 lg:py-20">
            {/* Story cards - now first on mobile */}
            <div className="flex-1 mb-12 lg:mb-0 order-1 lg:order-2">
              <div className="grid grid-cols-2 gap-6">
                {stories.map((story, index) => (
                  <div 
                    key={index}
                    className="relative w-full aspect-[4/5] rounded-2xl md:rounded-3xl lg:rounded-[32px] overflow-hidden group"
                  >
                    <img 
                      src={story.image} 
                      alt={story.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    {/* Audio overlay */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                      <button 
                        onClick={() => handleAudioPlay(index)}
                        className="group flex items-center justify-center gap-1 rounded-full bg-[#EDD1BA]/80 px-4 py-2 text-[#343434] text-[0.875rem] font-bold leading-[150%] relative overflow-hidden hover:scale-110 transition-all duration-300"
                        style={{
                          fontFamily: 'Aleo',
                          fontWeight: 700,
                          lineHeight: '1.5rem',
                          fontStyle: 'normal',
                          textEdge: 'cap',
                          leadingTrim: 'both',
                        }}
                      >
                        {/* Progress bar overlay */}
                        {activeAudioIndex === index && (
                          <div 
                            className="absolute left-0 top-0 h-full bg-[#7785FC] transition-all duration-100 ease-linear z-0 rounded-full"
                            style={{ width: `${audioProgress}%` }}
                          />
                        )}
                        
                        {/* Button content */}
                        <div className="relative z-10 flex items-center justify-center gap-1">
                          <div className="transition-opacity duration-300">
                            {activeAudioIndex === index && isAudioPlaying ? (
                              <Pause size={14} className="relative bottom-[1px] fill-current" />
                            ) : (
                              <Play size={14} className="relative bottom-[1px] fill-current" />
                            )}
                          </div>
                          <span className="text-[#343434]">
                            Hörprobe
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Text content - now second on mobile */}
            <div className="flex-1 order-2 lg:order-1">
              <h2 className="text-[#7785FC] text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] md:mb-4">
                LOS GEHT'S
              </h2>
              <h2 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] font-freeman mb-6 md:mb-12">
                So wird dein Kind sanft müde
              </h2>
              <p className="text-[#343434] text-lg md:text-xl font-aleo leading-relaxed mb-8">
                Unsere Hörgeschichten können individuell für die Schlafgewohnheiten deines Kindes angepasst werden. Das bedeutet, dass wir beruhigende Elemente und Lieblingsmotive einbauen können.
              </p>
              <a 
                href="/erstellen"
                className="h-14 bg-[#FFA23E] text-[#343434] px-6 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300"
              >
                Jetzt Hörgeschichte erstellen
              </a>
            </div>
          </div>
        </div>

        {/* Bottom decorative image */}
        <div className="w-full absolute -bottom-[1px] scale-x-[-1] select-none pointer-events-none" aria-hidden="true">
          <img
            src="https://i.imgur.com/DHRxkYk.png"
            alt=""
            className="w-full"
          />
        </div>
      </motion.section>

      {/* How Our Stories Help Sleep */}
      <motion.section
        className="w-full bg-[#EDD1BA] py-12 md:py-56"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      ><div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6">
      <h2 className="text-[#7785FC] text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] text-center mb-4">
        ENTSPANNT EINSCHLAFEN
      </h2>
      <h3 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] text-center font-freeman mb-12">
        Ein gesunder Schlaf ist das wertvollste Geschenk für die Entwicklung deines Kindes.
      </h3>
      
      <div className="grid md:grid-cols-2 gap-8 md:gap-12">
        <div className="bg-[#FFF3DD] rounded-3xl p-8">
          <h4 className="text-[#343434] text-[1.5rem] md:text-[2rem] leading-[110%] tracking-[-0.045rem] font-freeman mb-4">
            Abendroutine stärken
          </h4>
          <p className="text-[#343434] text-base md:text-lg font-aleo leading-relaxed">
            Kinder mit festen Einschlafritualen schlafen nachweislich besser ein und durch. Personalisierte Geschichten schaffen eine vertraute, beruhigende Atmosphäre und helfen dabei, den Tag sanft ausklingen zu lassen.
          </p>
          <a 
            href="/#warum-plottie"
            className="text-[#343434] hover:text-[#6674eb] transition-colors mt-4 inline-flex items-center gap-2 font-aleo font-bold group"
          >
            Mehr erfahren
            <ArrowRight 
              size={20}
              className="transform transition-transform duration-300 group-hover:translate-x-1"
            />
          </a>
        </div>
        
        <div className="bg-[#FFF3DD] rounded-3xl p-8">
          <h4 className="text-[#343434] text-[1.5rem] md:text-[2rem] leading-[110%] tracking-[-0.045rem] font-freeman mb-4">
            Geborgenheit schenken
          </h4>
          <p className="text-[#343434] text-base md:text-lg font-aleo leading-relaxed">
            In unseren Hörgeschichten wird dein Kind von vertrauten Elementen begleitet. Ob der Lieblingsteddy auf Traumreise geht oder das eigene Kinderzimmer zum magischen Schlafreich wird - jede Geschichte schafft eine sichere, geborgene Atmosphäre.
          </p>
          <a 
            href="/#warum-plottie"
            className="text-[#343434] hover:text-[#6674eb] transition-colors mt-4 inline-flex items-center gap-2 font-aleo font-bold group"
          >
            Mehr erfahren
            <ArrowRight 
              size={20}
              className="transform transition-transform duration-300 group-hover:translate-x-1"
            />
          </a>
        </div>
      </div>
      <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 text-center mt-24">
        <p className="text-[#343434] text-lg md:text-xl font-aleo leading-relaxed mb-12 max-w-3xl mx-auto">
          Erstelle jetzt in wenigen Minuten eine beruhigende Einschlafgeschichte für dein Kind. Einfach, individuell, entspannend.
        </p>
        <a 
          href="/erstellen"
          className="h-14 bg-[#FFA23E] text-[#343434] px-8 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300"
        >
          Jetzt entspannt einschlafen
        </a>
      </div>
    </div>
        
      </motion.section>

      {/* Testimonials Section */}
      <motion.section
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="w-full py-40 md:py-56"
      >
        <h2 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] text-center font-freeman pb-12 md:pb-4">
          Das sagen Eltern über Plottie.
        </h2>
        <Testimonials />
      </motion.section>

      {/* Final CTA Section */}
      <motion.section
        className="w-full bg-[#EDD1BA] py-24 pb-36 relative"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: 'url(https://i.imgur.com/ZNj20Aq.png)',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain'
          }}
        />
        
        {/* Content */}
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-[#343434] text-[2.5rem] md:text-[4rem] leading-[110%] tracking-[-0.075rem] md:tracking-[-0.12rem] font-freeman mb-6">
            Entspannte Abende für die ganze Familie
          </h2>
          <a 
            href="/erstellen"
            className="h-14 bg-[#FFF3DD] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#7785FC] transition-all duration-300"
          >
            Jetzt entspannt einschlafen
          </a>
        </div>
      </motion.section>

      {/* SEO Meta Tags */}
      <Helmet>
        <title>Einschlafgeschichten für Kinder – Personalisierte Geschichten zum Einschlafen | Plottie</title>
        <meta name="description" content="Entdecke personalisierte Einschlafgeschichten für dein Kind. Schaffe entspannte Abendrituale und eine beruhigende Einschlafzeit mit individuellen Geschichten." />
        <meta name="keywords" content="Einschlafgeschichten, Gute Nacht Geschichten, personalisierte Geschichten, Abendritual, Einschlafritual, Kinderschlaf" />
      </Helmet>
    </>
  );
};

export default SleepLanding; 